import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import QRCode from 'qrcode';
import {
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@mui/icons-material";
import { toast } from 'react-toastify';

import connectionService from "../../services/connectionService";
import queueService from "../../services/queueService";

// Schema de validação (sem alterações)
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Nome deve ter pelo menos 3 caracteres")
    .required("Nome é obrigatório"),
  queueId: Yup.number()
    .required("Fila é obrigatória"),
  // ... (outras validações)
  maxConnections: Yup.number()
    .min(1, "Mínimo 1 conexão")
    .max(100, "Máximo 100 conexões")
    .required("Número máximo de conexões é obrigatório"),
  sessionTimeout: Yup.number()
    .min(5, "Timeout mínimo de 5 minutos")
    .max(1440, "Timeout máximo de 24 horas")
    .required("Timeout da sessão é obrigatório")
});

/**
 * Componente de Modal para Conexão WhatsApp Não Oficial.
 * @param {object} props
 * @param {boolean} props.open - Controla se o modal está aberto.
 * @param {function} props.onClose - Função para fechar o modal.
 * @param {object} props.connection - Objeto da conexão (se estiver editando).
 * @param {function} props.onSave - Função para salvar/atualizar a conexão.
 * @param {object} props.currentUser - Objeto do usuário logado (ex: { id, companyId }).
 */
const UnofficialConnectionModal = ({ open, onClose, connection, onSave, currentUser }) => {
  const [qrCode, setQrCode] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [managedConnection, setManagedConnection] = useState(null);
  const managedConnectionId = managedConnection?.id || connection?.id || null;

  const connectionStatusRef = useRef(connectionStatus);
  const timeoutRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // Valores iniciais memoizados para o Formik
  const initialValues = useMemo(() => ({
    name: connection?.name || "",
    queueId: connection?.queueId || "",
    greetingMessage: connection?.greetingMessage || "Olá! Como posso ajudá-lo?",
    farewellMessage: connection?.farewellMessage || "Obrigado pelo contato!",
    outOfHoursMessage: connection?.outOfHoursMessage || "No momento estamos fora do horário de atendimento.",
    maxConnections: connection?.maxConnections || 50,
    sessionTimeout: connection?.sessionTimeout || 30,
    isActive: connection?.isActive || true,
    autoReconnect: connection?.autoReconnect !== false,
    messageCategory: connection?.messageCategory || 'service',
    isDefault: connection?.isDefault || false // Garante que temos o campo
  }), [connection]);

  // Carregar filas disponíveis
  useEffect(() => {
    const fetchQueues = async () => {
      if (open) {
        setLoadingQueues(true);
        try {
          const queueList = await queueService.getQueues();
          const normalized = Array.isArray(queueList)
            ? queueList
            : (Array.isArray(queueList?.data)
              ? queueList.data
              : (Array.isArray(queueList?.queues)
                ? queueList.queues
                : []));
          setQueues(normalized);
        } catch (error) {
          console.error("Erro ao carregar filas:", error);
        } finally {
          setLoadingQueues(false);
        }
      }
    };
    fetchQueues();
  }, [open]);


  const normalizeSavedConnection = (resp) => {
    const raw = resp?.session ?? resp?.data?.session ?? resp?.data ?? resp;
    return raw && typeof raw === 'object' ? raw : null;
  };

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const generateQRCode = useCallback(async (values) => {
    const nameValue = typeof values?.name === 'string' ? values.name.trim() : '';
    const queueValue = values?.queueId;
    if (!nameValue) {
      setError('Nome da conexão é obrigatório');
      setConnectionStatus("disconnected");
      setLoading(false);
      return;
    }
    if (!queueValue) {
      setError('Fila é obrigatória');
      setConnectionStatus("disconnected");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setQrCode("");

    clearTimers();

    try {
      const resp = await connectionService.generateQRCode({ name: nameValue, queueId: queueValue });
      const savedConnection = resp?.data || resp?.session || resp;
      const connectionId = savedConnection?.id;
      if (!connectionId) throw new Error("Falha ao salvar conexão para gerar QR Code");

      setManagedConnection({ id: connectionId, name: savedConnection?.name || nameValue, queueId: queueValue, type: 'unofficial' });

      await connectionService.startSession(connectionId);

      setConnectionStatus("connecting");

      const pollOnce = async () => {
        const qr = await connectionService.getQRCode(connectionId);
        const code = typeof qr?.qrCode === 'string' ? qr.qrCode.trim() : '';
        const status = qr?.status ? String(qr.status) : '';

        if (code) {
          setQrCode(code);
          setConnectionStatus("qr_ready");
          setLoading(false);
        }

        if (status === 'CONNECTED') {
          setConnectionStatus("connected");
          setSessionInfo({ id: connectionId, name: savedConnection?.name || nameValue, status });
          setLoading(false);
          clearTimers();
        }
      };

      await pollOnce().catch(() => {});

      pollRef.current = setInterval(() => {
        pollOnce().catch(() => {});
      }, 2000);

      timeoutRef.current = setTimeout(() => {
        clearTimers();
        const currentStatus = connectionStatusRef.current;
        if (currentStatus !== "connected" && currentStatus !== "qr_ready") {
          setConnectionStatus("disconnected");
          setError("Timeout na geração do QR Code. Tente novamente.");
          setLoading(false);
        }
      }, 60000);
    } catch (err) {
      setError(err.message || "Erro ao gerar QR Code");
      setConnectionStatus("disconnected");
      toast.error("Erro ao gerar QR Code");
      setLoading(false);
    }
  }, []); 


  // **NOVO HOOK: Gerencia o estado ao abrir o modal (Novo vs Edição)**
  useEffect(() => {
    // Se o modal não estiver aberto, limpa timeouts
    if (!open) {
      clearTimers();
      return;
    }

    // MODO DE EDIÇÃO: Carrega dados da 'connection'
    if (connection?.id) {
      setManagedConnection(connection);
      setConnectionStatus(connection.status || 'disconnected');
      // @ts-ignore
      setSessionInfo(connection.sessionInfo || null); // Assumindo que sessionInfo possa vir
      setQrCode(connection.qrcode || connection.qrCode || ""); // Carrega QR antigo se existir
      setError("");
      setLoading(false);

    // MODO DE NOVA CONEXÃO: Apenas limpa estado (não gera QR)
    } else {
      setManagedConnection(null);
      setQrCode("");
      setConnectionStatus("disconnected");
      setError("");
      setSessionInfo(null);
    }

  }, [open, connection, initialValues, generateQRCode]);


  // Função de Refresh (agora com 'useCallback' e recebe 'currentFormValues')
  const handleRefreshQR = useCallback((currentFormValues) => {
    setQrCode("");
    setConnectionStatus("disconnected");
    setError("");
    
    setTimeout(() => {
      // Usa os valores atuais do formulário, ou os valores iniciais como fallback
      const valuesToUse = currentFormValues || initialValues;
      generateQRCode(valuesToUse);
    }, 500);
  }, [generateQRCode, initialValues]);


  // Funções de Status (sem alterações)
  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected": return "success";
      case "connecting": case "qr_ready": return "warning";
      default: return "error";
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "connected": return "Conectado";
      case "connecting": return "Conectando...";
      case "qr_ready": return "QR Code Pronto";
      default: return "Desconectado";
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case "connected": return <CheckCircleIcon />;
      case "connecting": case "qr_ready": return <QrCodeIcon />;
      default: return <ErrorIcon />;
    }
  };

  // Handle Submit: persiste via service e entrega conexão salva ao onSave
  const handleSubmit = async (values) => {
    try {
      console.log('🔄 [UnofficialConnectionModal] Iniciando salvamento da conexão');

      const connectionData = {
        ...values,
        type: "unofficial",
        status: connectionStatus,
        sessionId: sessionInfo?.sessionId || sessionInfo?.name || null,
        qrCode: connectionStatus === "qr_ready" ? qrCode : null,
        isDefault: values.isDefault || false
      };

      console.log('🔄 [UnofficialConnectionModal] Connection Data:', connectionData);

      let saved;
      if (managedConnectionId) {
        // Atualização
        saved = await connectionService.updateConnection(managedConnectionId, connectionData);
      } else {
        // Criação
        saved = await connectionService.createConnection(connectionData);
      }

      // Normaliza retorno para objeto de conexão
      const savedConnection = normalizeSavedConnection(saved);
      console.log('✅ [UnofficialConnectionModal] Conexão salva:', savedConnection);

      // Entrega a conexão salva para o caller (página)
      if (savedConnection) setManagedConnection(savedConnection);
      await onSave(savedConnection);

      onClose();
    } catch (err) {
      console.error('❌ [UnofficialConnectionModal] Erro ao salvar conexão:', err);
      setError(err.message || "Erro ao salvar conexão");
    }
  };

  // JSX/Render
  return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeIcon />
          <Typography variant="h6">
            {connection?.id ? 'Editar' : 'Nova'} Conexão WhatsApp (Não Oficial)
          </Typography>
        </Box>
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize // Permite que o form reset quando 'initialValues' mudar
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: 600, maxWidth: 800, padding: 3 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 2 }}>
                <Typography variant="subtitle1">Status da Conexão:</Typography>
                <Chip
                  icon={getStatusIcon()}
                  label={getStatusText()}
                  color={getStatusColor()}
                  sx={{ margin: "8px 0" }}
                />
              </Box>

              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 3,
                backgroundColor: "grey.50",
                borderRadius: 1,
                margin: "16px 0"
              }}>
                <Typography variant="h6" gutterBottom>
                  {connectionStatus === 'connected' ? 'Conexão Estabelecida' : 'Escaneie o QR Code'}
                </Typography>
                
                <Box sx={{
                  padding: 2,
                  backgroundColor: "white",
                  borderRadius: 1,
                  boxShadow: 2
                }}>
                  {loading || connectionStatus === 'connecting' ? (
                    <Box sx={{ 
                      width: 256, 
                      height: 256, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <CircularProgress size={60} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {connectionStatus === 'connecting' ? 'Aguardando QR Code...' : 'Gerando...'}
                      </Typography>
                    </Box>
                  ) : (
                    <QRCodeDisplay value={qrCode} size={256} status={connectionStatus} />
                  )}
                </Box>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  // **CORREÇÃO: Passa os 'values' atuais do Formik para o handler**
                  onClick={() => handleRefreshQR(values)} 
                  disabled={loading || connectionStatus === 'connected' || connectionStatus === 'connecting'}
                  sx={{ marginTop: 1 }}
                >
                  {loading || connectionStatus === 'connecting' ? 'Gerando...' : 'Gerar Novo QR Code'}
                </Button>
              </Box>

              {/* Campos do formulário */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Nome da Conexão"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={touched.queueId && Boolean(errors.queueId)}>
                    <InputLabel>Fila *</InputLabel>
                    <Select
                      name="queueId"
                      value={values.queueId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Fila *"
                      disabled={loadingQueues}
                    >
                      {queues.map((queue) => (
                        <MenuItem key={queue.id} value={queue.id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.queueId && errors.queueId && (
                      <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                        {errors.queueId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      name="messageCategory"
                      value={values.messageCategory}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Tipo"
                    >
                      <MenuItem value="billing">Cobrança</MenuItem>
                      <MenuItem value="otp">OTP</MenuItem>
                      <MenuItem value="service">Serviço</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Outros TextFields (greeting, farewell, etc.) ... */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    name="greetingMessage"
                    label="Mensagem de Saudação"
                    value={values.greetingMessage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                    helperText={touched.greetingMessage && errors.greetingMessage}
                  />
                </Grid>
                
                {/* ... (farewellMessage, outOfHoursMessage) ... */}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    name="maxConnections"
                    label="Máximo de Conexões"
                    value={values.maxConnections}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxConnections && Boolean(errors.maxConnections)}
                    helperText={touched.maxConnections && errors.maxConnections}
                    inputProps={{ min: 1, max: 100 }}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    name="sessionTimeout"
                    label="Timeout da Sessão (minutos)"
                    value={values.sessionTimeout}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.sessionTimeout && Boolean(errors.sessionTimeout)}
                    helperText={touched.sessionTimeout && errors.sessionTimeout}
                    inputProps={{ min: 5, max: 1440 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={values.isActive}
                        onChange={handleChange}
                      />
                    }
                    label="Conexão Ativa"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="autoReconnect"
                        checked={values.autoReconnect}
                        onChange={handleChange}
                      />
                    }
                    label="Reconexão Automática"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ padding: 3, gap: 1 }}>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || loadingQueues}
                sx={{ position: 'relative' }}
              >
                {connection ? "Atualizar" : "Criar"} Conexão
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'primary.main',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px'
                    }}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

// Componente QRCodeDisplay (sem alterações)
const QRCodeDisplay = ({ value, size = 256, status }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    
    if (canvasRef.current && value) {
      QRCode.toCanvas(canvasRef.current, value, {
        width: size,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      }).then(() => {
      }).catch(err => {
        console.error('🖼️ [QRCodeDisplay] Erro ao gerar QR Code:', err);
      });
    }
  }, [value, size]);

  if (!value) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed #ccc',
          borderRadius: 1,
          backgroundColor: '#f5f5f5',
          flexDirection: 'column'
        }}
      >
        <QrCodeIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
        <Typography variant="body2" color="textSecondary" align="center">
          {status === 'connecting' ? 'Aguardando QR Code...' : 'QR Code não disponível'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <canvas ref={canvasRef} style={{ borderRadius: 8 }} />
      {status === 'connected' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(76, 175, 80, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1
          }}
        >
          <CheckCircleIcon sx={{ color: 'white', fontSize: 48 }} />
        </Box>
      )}
    </Box>
  );
};

export default UnofficialConnectionModal;
